import { useEffect, useState } from 'react';
import { Browser } from '@capacitor/browser';
import { Link } from 'react-router-dom';
import {
  useMe, useEnrollments, useSubscription, useUpdateProfile, useBillingConfig,
  useReferralInfo, requestAccountDeletion,
} from '../lib/api';
import { useAuth } from '../lib/auth';
import { getThemePreference, setThemePreference, type ThemePreference } from '../lib/theme';
import { AvatarUpload } from '../components/AvatarUpload';
import { VerifiedBadge } from '../components/VerifiedBadge';
import {
  configureRevenueCat,
  getNativeCustomerInfo,
  getNativeOfferings,
  hasActivePro,
  isNativeBillingPlatform,
  onNativeCustomerInfoUpdated,
  packageForBillingPlan,
  presentNativeCustomerCenter,
  purchaseNativePackage,
  restoreNativePurchases,
} from '../lib/native-billing';
import type { BillingConfigResponse, BillingPlan } from '@shared/types';
import { PageTransition, Pressable, Modal, CountUp } from '../lib/motion';

const CARD = 'bg-white rounded-2xl border border-slate-200/80 shadow-card';

export function ProfilePage() {
  const { signOut } = useAuth();
  const { data: me } = useMe();
  const { data: enrollments } = useEnrollments();
  const { data: referralData } = useReferralInfo();
  const [showAllReferrals, setShowAllReferrals] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!me) return null;
  const u = me.user;
  const s = me.stats;
  const initials = `${u.firstName[0] ?? ''}${u.lastName[0] ?? ''}`.toUpperCase() || '?';
  const referralPoints = (s as any).referralPoints ?? referralData?.balance ?? 0;
  const referrals = referralData?.referrals ?? [];

  return (
    <PageTransition className="space-y-5 max-w-3xl">
      {/* ── Profile header ─────────────────────────────────── */}
      <section className="relative overflow-hidden bg-white rounded-3xl border border-slate-200/80 shadow-card">
        <div className="relative h-24 bg-gradient-to-r from-squishy-navy via-squishy-navySoft to-squishy-purple overflow-hidden" aria-hidden="true">
          <div className="absolute -top-10 -right-6 w-44 h-44 rounded-full bg-squishy-teal/25 blur-2xl" />
          <div className="absolute -bottom-12 left-1/4 w-36 h-36 rounded-full bg-squishy-purple/40 blur-2xl" />
          <div className="absolute top-4 right-8 w-1.5 h-1.5 rounded-full bg-white/50" />
          <div className="absolute top-9 right-20 w-1 h-1 rounded-full bg-white/40" />
          <div className="absolute top-6 left-10 w-1 h-1 rounded-full bg-white/30" />
        </div>
        <div className="px-5 sm:px-6 pb-5">
          <div className="flex items-end justify-between -mt-10">
            <div className="rounded-full bg-white p-1 shadow-card">
              <AvatarUpload initials={initials} currentKey={u.avatarS3Key} />
            </div>
            {u.verifiedStudent && (
              <span className="mb-1 inline-flex items-center gap-1 text-xs font-semibold text-squishy-tealDark bg-teal-50 rounded-full px-2.5 py-1">
                <VerifiedBadge size={12} /> Verified Student
              </span>
            )}
          </div>
          <div className="mt-3 flex items-center gap-1.5 min-w-0">
            <span className="font-display text-xl font-semibold text-squishy-dark tracking-tight truncate">{u.displayName}</span>
            {u.verifiedStudent && <VerifiedBadge size={18} />}
          </div>
          <div className="text-sm text-slate-500 truncate">{u.email}</div>

          <div className="mt-5 grid grid-cols-4 divide-x divide-slate-100 rounded-2xl bg-slate-50 py-3">
            <Stat label="Level" value={s.level} color="text-squishy-primary" />
            <Stat label="XP" value={s.totalXp} color="text-violet-600" />
            <Stat label="Streak" value={s.currentStreak} format={(n) => `${n}d`} color="text-amber-600" />
            <Stat label="Referral Pts" value={Number(referralPoints) || 0} color="text-emerald-600" />
          </div>
        </div>
      </section>

      <SettingsPanel />

      <AppearancePanel />

      {/* ── My classes ─────────────────────────────────────── */}
      <section className={`${CARD} p-6`}>
        <h2 className="font-display text-base font-semibold text-squishy-dark tracking-tight mb-2">My Classes</h2>
        {enrollments?.enrollments?.length ? (
          <ul className="divide-y divide-slate-100">
            {enrollments.enrollments.map((e: any) => (
              <li key={e.id}>
                <Link to={`/class/${e.id}`} className="group flex items-center justify-between gap-3 py-3 min-h-[44px]">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-squishy-dark truncate group-hover:text-squishy-tealDark transition-colors">
                      {e.school_name} · {e.class_code}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {e.professor_name ? `Prof. ${e.professor_name}` : 'All teachers'} · {e.term}
                    </div>
                  </div>
                  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 shrink-0 text-slate-300 group-hover:text-squishy-tealDark transition-colors">
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <Link to="/onboarding" className="inline-flex items-center gap-1 min-h-[44px] text-sm font-semibold text-squishy-primary">
            + Add a class
          </Link>
        )}
      </section>

      {/* ── Referrals ──────────────────────────────────────── */}
      <section className={`${CARD} p-6`}>
        <h2 className="font-display text-base font-semibold text-squishy-dark tracking-tight mb-2">Referrals</h2>
        {referrals.length > 0 ? (
          <>
            <ul className="divide-y divide-slate-100">
              {referrals.slice(0, 2).map((r: any) => (
                <li key={r.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 shrink-0 rounded-full bg-teal-50 text-squishy-tealDark flex items-center justify-center text-xs font-bold">
                      {(r.first_name?.[0] ?? '?').toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-squishy-dark truncate">{r.first_name} {r.last_name}</span>
                  </div>
                  <span className="shrink-0 text-[11px] font-medium capitalize text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{r.subscription_tier}</span>
                </li>
              ))}
            </ul>
            {referrals.length > 2 && (
              <button
                onClick={() => setShowAllReferrals(true)}
                className="mt-1 inline-flex items-center min-h-[44px] text-xs font-semibold text-squishy-primary hover:underline"
              >
                +{referrals.length - 2} more
              </button>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-500">Share questions to invite friends and earn referral points.</p>
        )}
        {referralData?.referral_url && (
          <Pressable
            haptic="success"
            onClick={() => {
              navigator.clipboard.writeText(referralData.referral_url);
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            }}
            className={`mt-3 inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-xl text-sm font-semibold transition-colors ${
              copied ? 'bg-emerald-50 text-emerald-600' : 'bg-teal-50 text-squishy-tealDark active:bg-teal-100'
            }`}
          >
            {copied ? (
              <>
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path d="M7 3.5A1.5 1.5 0 018.5 2h3.879a1.5 1.5 0 011.06.44l3.122 3.12A1.5 1.5 0 0117 6.622V12.5a1.5 1.5 0 01-1.5 1.5h-1v-3.379a3 3 0 00-.879-2.121L10.5 5.379A3 3 0 008.379 4.5H7v-1z" />
                  <path d="M4.5 6A1.5 1.5 0 003 7.5v9A1.5 1.5 0 004.5 18h7a1.5 1.5 0 001.5-1.5v-5.879a1.5 1.5 0 00-.44-1.06L9.44 6.439A1.5 1.5 0 008.378 6H4.5z" />
                </svg>
                Copy referral link
              </>
            )}
          </Pressable>
        )}
      </section>

      <SubscriptionPanel />

      {/* ── Legal ──────────────────────────────────────────── */}
      <section className={`${CARD} p-6`}>
        <h2 className="font-display text-base font-semibold text-squishy-dark tracking-tight mb-2">Legal</h2>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
          <Link to="/terms" className="py-2 text-slate-500 hover:text-squishy-primary hover:underline">Terms of Service</Link>
          <Link to="/privacy" className="py-2 text-slate-500 hover:text-squishy-primary hover:underline">Privacy Policy</Link>
          <Link to="/cookies" className="py-2 text-slate-500 hover:text-squishy-primary hover:underline">Cookie Policy</Link>
        </div>
      </section>

      <DeleteAccountSection />

      <Pressable
        onClick={signOut}
        className={`${CARD} w-full min-h-[48px] text-sm font-medium text-slate-500 hover:text-red-600 active:bg-slate-50 transition-colors`}
      >
        Sign out
      </Pressable>

      {/* ── All referrals sheet ────────────────────────────── */}
      <Modal open={showAllReferrals} onClose={() => setShowAllReferrals(false)} sheet="bottom">
        <div className="bg-white rounded-t-3xl max-h-[70vh] overflow-y-auto p-5 pb-8">
          <div className="flex justify-center pb-3" aria-hidden="true">
            <div className="w-10 h-1 rounded-full bg-slate-200" />
          </div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-lg font-semibold text-squishy-dark tracking-tight">All Referrals</h2>
            <Pressable
              onClick={() => setShowAllReferrals(false)}
              aria-label="Close"
              className="w-11 h-11 -mr-2 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 active:bg-slate-100 transition-colors"
            >
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
              </svg>
            </Pressable>
          </div>
          <ul className="divide-y divide-slate-100">
            {[...referrals].sort((a: any, b: any) => (a.first_name + a.last_name).localeCompare(b.first_name + b.last_name)).map((r: any) => (
              <li key={r.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 shrink-0 rounded-full bg-teal-50 text-squishy-tealDark flex items-center justify-center text-xs font-bold">
                    {(r.first_name?.[0] ?? '?').toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <span className="block text-sm font-medium text-squishy-dark truncate">{r.first_name} {r.last_name}</span>
                    <span className="block text-xs text-slate-400">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <span className="shrink-0 text-[11px] font-medium capitalize text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">{r.subscription_tier}</span>
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    </PageTransition>
  );
}

function SubscriptionPanel() {
  const { data: me } = useMe();
  const { data: subscription } = useSubscription();
  const { data: config } = useBillingConfig();
  const [nativeReady, setNativeReady] = useState(false);
  const [nativeBusy, setNativeBusy] = useState<string | null>(null);
  const [nativeError, setNativeError] = useState<string | null>(null);
  const [nativePackages, setNativePackages] = useState<Record<string, any>>({});
  const [nativeManageUrl, setNativeManageUrl] = useState<string | null>(null);
  const [nativeHasPro, setNativeHasPro] = useState(false);

  const user = me?.user;
  const native = isNativeBillingPlatform();

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | undefined;
    async function loadNativeBilling() {
      if (!native || !user || !config) return;
      setNativeError(null);
      try {
        const configured = await configureRevenueCat(user, config);
        if (!active) return;
        setNativeReady(configured);
        if (!configured) {
          setNativeError('In-app purchases are being configured for this app build.');
          return;
        }
        const [offerings, info] = await Promise.all([
          getNativeOfferings(),
          getNativeCustomerInfo().catch(() => null),
        ]);
        if (!active) return;
        const packagesByPlan: Record<string, any> = {};
        for (const plan of config.plans ?? fallbackPlans()) {
          packagesByPlan[plan.id] = packageForBillingPlan(offerings, plan);
        }
        setNativePackages(packagesByPlan);
        setNativeManageUrl(info?.managementURL ?? null);
        setNativeHasPro(hasActivePro(info, config.revenueCat.entitlementId));
        unsubscribe = onNativeCustomerInfoUpdated((customerInfo) => {
          setNativeManageUrl(customerInfo.managementURL ?? null);
          setNativeHasPro(hasActivePro(customerInfo, config.revenueCat.entitlementId));
        });
      } catch (err: any) {
        if (!active) return;
        setNativeReady(false);
        setNativeError(err?.message ?? 'Native billing could not be loaded.');
      }
    }
    loadNativeBilling();
    return () => {
      active = false;
      unsubscribe?.();
    };
  }, [native, user, config]);

  if (!user) return null;

  const displayedTier = nativeHasPro ? 'pro' : (subscription?.tier ?? user.subscriptionTier);
  const actualTier = subscription?.actualTier ?? config?.actualTier ?? user.subscriptionTier;

  return (
    <section className={`${CARD} p-6 space-y-4`}>
      <div>
        <h2 className="font-display text-base font-semibold text-squishy-dark tracking-tight">Subscription</h2>
        <p className="text-sm text-slate-500 mt-1">
          Current plan: <strong className="capitalize text-squishy-dark">{displayedTier}</strong>
          {subscription?.status && <span className="ml-2">({subscription.status})</span>}
        </p>
        <p className="text-xs text-slate-500 mt-1">
          Squishy School Pro is an auto-renewable subscription. New mobile subscribers get a 30-day free trial, then it renews monthly ($4.99 / month) or yearly ($49.99 / year) at the price shown until canceled. Payment is charged to your App Store or Google Play account, and the subscription renews automatically unless canceled at least 24 hours before the end of the current period. Manage or cancel anytime in your app store account settings.
        </p>
        <p className="text-xs text-slate-500 mt-2">
          <Link to="/terms" className="text-squishy-primary hover:underline">Terms of Use (EULA)</Link>
          <span className="mx-1.5 text-slate-300">·</span>
          <Link to="/privacy" className="text-squishy-primary hover:underline">Privacy Policy</Link>
        </p>
      </div>

      {native ? (
        <>
          <div className="rounded-2xl border border-squishy-primary/20 bg-squishy-primary/5 p-4 space-y-3">
            <div>
              <div className="font-display font-semibold text-squishy-dark tracking-tight">Squishy School Pro</div>
              <div className="text-sm text-slate-600">Start a 30-day free trial, then choose monthly or yearly in the secure app store checkout.</div>
            </div>
            <button
              className="w-full bg-squishy-primary text-white px-4 min-h-[48px] rounded-xl text-sm font-semibold shadow-pop transition active:scale-[0.99] disabled:opacity-50 disabled:shadow-none"
              disabled={!nativeReady || nativeBusy === 'paywall' || (!nativePackages.pro_monthly && !nativePackages.pro_annual)}
              onClick={async () => {
                // Direct StoreKit/Play purchase via the native store sheet (shows price, the
                // 30-day intro trial, and terms). Avoids RevenueCat's hosted paywall, which
                // requires a separately-configured paywall design and otherwise errors.
                const pkg = nativePackages.pro_monthly ?? nativePackages.pro_annual;
                if (!pkg) {
                  setNativeError('Subscriptions are not available right now. Please try again in a moment.');
                  return;
                }
                setNativeBusy('paywall');
                setNativeError(null);
                try {
                  const info = await purchaseNativePackage(pkg);
                  setNativeManageUrl(info.managementURL ?? null);
                  setNativeHasPro(hasActivePro(info, config?.revenueCat.entitlementId ?? 'pro'));
                } catch (err: any) {
                  if (!err?.userCancelled) setNativeError(err?.message ?? 'Purchase did not complete.');
                } finally {
                  setNativeBusy(null);
                }
              }}
            >
              {nativeBusy === 'paywall' ? 'Starting...' : 'Start 30-day free trial'}
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {(config?.plans ?? fallbackPlans()).map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                nativeReady={nativeReady}
                nativePackage={nativePackages[plan.id]}
                busy={nativeBusy === plan.id}
                onNativePurchase={async () => {
                  const pkg = nativePackages[plan.id];
                  if (!pkg) {
                    setNativeError(`${plan.label} is not available in the store yet.`);
                    return;
                  }
                  setNativeBusy(plan.id);
                  setNativeError(null);
                  try {
                    const info = await purchaseNativePackage(pkg);
                    setNativeManageUrl(info.managementURL ?? null);
                    setNativeHasPro(hasActivePro(info, config?.revenueCat.entitlementId ?? 'pro'));
                  } catch (err: any) {
                    if (!err?.userCancelled) setNativeError(err?.message ?? 'Purchase did not complete.');
                  } finally {
                    setNativeBusy(null);
                  }
                }}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="rounded-2xl border border-squishy-primary/20 bg-squishy-primary/5 p-4 text-sm text-slate-600">
          Pro subscriptions are managed through the Squishy School iOS or Android app. Open Squishy School on your phone to start the 30-day free trial, upgrade, restore purchases, or manage your subscription.
        </div>
      )}

      {nativeError && <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">{nativeError}</div>}

      <div className="flex flex-wrap gap-2">
        {native ? (
          <>
            <button
              className="border border-slate-200 px-4 min-h-[44px] rounded-xl text-sm font-medium text-slate-600 active:bg-slate-50 transition-colors disabled:opacity-50"
              disabled={!nativeReady || nativeBusy === 'restore'}
              onClick={async () => {
                setNativeBusy('restore');
                setNativeError(null);
                try {
                  const info = await restoreNativePurchases();
                  setNativeManageUrl(info.managementURL ?? null);
                  setNativeHasPro(hasActivePro(info, config?.revenueCat.entitlementId ?? 'pro'));
                } catch (err: any) {
                  setNativeError(err?.message ?? 'Restore did not complete.');
                } finally {
                  setNativeBusy(null);
                }
              }}
            >
              {nativeBusy === 'restore' ? 'Restoring...' : 'Restore purchases'}
            </button>
            <button
              className="border border-slate-200 px-4 min-h-[44px] rounded-xl text-sm font-medium text-slate-600 active:bg-slate-50 transition-colors disabled:opacity-50"
              disabled={!nativeManageUrl}
              onClick={() => nativeManageUrl && Browser.open({ url: nativeManageUrl })}
            >
              Manage subscription
            </button>
            <button
              className="border border-slate-200 px-4 min-h-[44px] rounded-xl text-sm font-medium text-slate-600 active:bg-slate-50 transition-colors disabled:opacity-50"
              disabled={!nativeReady || nativeBusy === 'customer-center'}
              onClick={async () => {
                setNativeBusy('customer-center');
                setNativeError(null);
                try {
                  const info = await presentNativeCustomerCenter();
                  setNativeManageUrl(info?.managementURL ?? null);
                  setNativeHasPro(hasActivePro(info, config?.revenueCat.entitlementId ?? 'pro'));
                } catch (err: any) {
                  setNativeError(err?.message ?? 'Could not open subscription help.');
                } finally {
                  setNativeBusy(null);
                }
              }}
            >
              {nativeBusy === 'customer-center' ? 'Opening...' : 'Subscription help'}
            </button>
          </>
        ) : actualTier === 'pro' ? (
          <span className="text-sm text-slate-500">Manage this subscription in the mobile app store account that started it.</span>
        ) : null}
      </div>
    </section>
  );
}

function PlanCard({
  plan,
  nativeReady,
  nativePackage,
  busy,
  onNativePurchase,
}: {
  plan: BillingPlan;
  nativeReady: boolean;
  nativePackage: any;
  busy: boolean;
  onNativePurchase: () => Promise<void>;
}) {
  const displayPrice = nativePackage?.product?.priceString ?? plan.price;
  return (
    <div className="border border-slate-200/80 rounded-2xl p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-squishy-dark">{plan.label}</div>
          <div className="text-sm text-slate-500">{plan.tagline}</div>
        </div>
        {plan.badge && <span className="text-xs font-semibold bg-teal-50 text-squishy-tealDark px-2 py-1 rounded-full">{plan.badge}</span>}
      </div>
      <div>
        <span className="font-display text-2xl font-bold text-squishy-dark tracking-tight">{displayPrice}</span>
        <span className="text-sm text-slate-500"> / {plan.interval}</span>
        <div className="text-xs text-slate-500 mt-1">30 days free, then auto-renews. Cancel anytime in your app store account.</div>
      </div>
      {plan.features.length > 0 && (
        <ul className="space-y-1.5">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-xs text-slate-600">
              <svg className="w-3.5 h-3.5 mt-px shrink-0 text-squishy-primary" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.415l-7.01 7.01a1 1 0 01-1.415 0L3.296 8.732a1 1 0 111.414-1.414l3.276 3.276 6.303-6.303a1 1 0 011.415 0z" clipRule="evenodd" />
              </svg>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      )}
      <button
        className="w-full bg-squishy-primary text-white px-4 min-h-[44px] rounded-xl text-sm font-semibold shadow-pop transition active:scale-[0.99] disabled:opacity-50 disabled:shadow-none"
        disabled={busy || !nativeReady || !nativePackage}
        onClick={onNativePurchase}
      >
        {busy ? 'Working...' : 'Start 30-day free trial'}
      </button>
    </div>
  );
}

function fallbackPlans(): BillingConfigResponse['plans'] {
  return [
    {
      id: 'plus_monthly',
      tier: 'plus',
      interval: 'month',
      label: 'Plus Monthly',
      price: '$1.99',
      priceUsd: 1.99,
      productId: 'plus_monthly',
      stripePriceId: '',
      revenueCatPackageType: 'MONTHLY',
      tagline: 'For steady studying',
      features: [
        '15 AI generated questions per day (150 per month)',
        'Full flashcard study sessions',
        'Post, reply, and vote in discussions',
        '3 study group sessions per month',
        'No ads',
      ],
    },
    {
      id: 'plus_annual',
      tier: 'plus',
      interval: 'year',
      label: 'Plus Annual',
      price: '$19.99',
      priceUsd: 19.99,
      productId: 'plus_annual',
      stripePriceId: '',
      revenueCatPackageType: 'ANNUAL',
      badge: 'Save 16%',
      tagline: 'For steady studying',
      features: [
        '15 AI generated questions per day (150 per month)',
        'Full flashcard study sessions',
        'Post, reply, and vote in discussions',
        '3 study group sessions per month',
        'No ads',
      ],
    },
    {
      id: 'pro_monthly',
      tier: 'pro',
      interval: 'month',
      label: 'Pro Monthly',
      price: '$4.99',
      priceUsd: 4.99,
      productId: 'pro_monthly',
      stripePriceId: '',
      revenueCatPackageType: 'MONTHLY',
      tagline: 'Everything in Plus, and more',
      features: [
        '50 AI generated questions per day (500 per month)',
        'Pro explanations on every question',
        'Unlimited study group sessions',
        'Everything in Plus: flashcard sessions, discussions, no ads',
      ],
    },
    {
      id: 'pro_annual',
      tier: 'pro',
      interval: 'year',
      label: 'Pro Annual',
      price: '$49.99',
      priceUsd: 49.99,
      productId: 'pro_annual',
      stripePriceId: '',
      revenueCatPackageType: 'ANNUAL',
      badge: 'Best value',
      tagline: 'Everything in Plus, and more',
      features: [
        '50 AI generated questions per day (500 per month)',
        'Pro explanations on every question',
        'Unlimited study group sessions',
        'Everything in Plus: flashcard sessions, discussions, no ads',
      ],
    },
  ];
}

function DeleteAccountSection() {
  const { data: me } = useMe();
  const { signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const email = me?.user.email;
  if (!email) return null;

  return (
    <section className="bg-white rounded-2xl border border-red-200 shadow-card p-6">
      <h2 className="font-display text-base font-semibold text-red-700 tracking-tight">Delete account</h2>
      <p className="text-sm text-slate-500 mt-1">
        Permanently delete your Squishy School account and associated data. This cannot be undone.
      </p>
      <Pressable
        haptic="warning"
        onClick={() => { setOpen(true); setError(null); setDone(false); }}
        className="mt-3 border border-red-300 text-red-700 px-4 min-h-[44px] rounded-xl text-sm font-medium hover:bg-red-50 transition-colors"
      >
        Delete account
      </Pressable>

      <Modal open={open} onClose={() => { if (!busy) setOpen(false); }}>
        <div className="bg-white rounded-3xl p-6 space-y-4 shadow-pop">
          {done ? (
            <>
              <h3 className="font-display text-lg font-semibold text-squishy-dark tracking-tight">Account deletion requested</h3>
              <p className="text-sm text-slate-600">
                We have received your request. Your account ({email}) and associated data are scheduled for permanent deletion. You will now be signed out.
              </p>
              <Pressable
                onClick={() => { void signOut(); }}
                className="w-full bg-squishy-primary text-white px-4 min-h-[48px] rounded-xl text-sm font-semibold shadow-pop"
              >
                Sign out
              </Pressable>
            </>
          ) : (
            <>
              <h3 className="font-display text-lg font-semibold text-red-700 tracking-tight">Delete your account?</h3>
              <p className="text-sm text-slate-600">
                This permanently deletes your account ({email}), study history, and personal data. This action cannot be undone.
              </p>
              <p className="text-xs text-slate-500">
                Note: an active subscription is billed by Apple or Google and must be canceled separately in your App Store or Google Play account settings.
              </p>
              {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</div>}
              <div className="flex gap-2">
                <Pressable
                  disabled={busy}
                  onClick={() => setOpen(false)}
                  className="flex-1 border border-slate-200 px-4 min-h-[48px] rounded-xl text-sm font-medium text-slate-600 active:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </Pressable>
                <Pressable
                  haptic="heavy"
                  disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    setError(null);
                    try {
                      await requestAccountDeletion(email);
                      setDone(true);
                    } catch (e: any) {
                      setError(e?.message ?? 'Could not submit the deletion request. Please try again.');
                    } finally {
                      setBusy(false);
                    }
                  }}
                  className="flex-1 bg-red-600 text-white px-4 min-h-[48px] rounded-xl text-sm font-semibold disabled:opacity-50"
                >
                  {busy ? 'Deleting...' : 'Delete account'}
                </Pressable>
              </div>
            </>
          )}
        </div>
      </Modal>
    </section>
  );
}

function Stat({ label, value, format, color }: {
  label: string;
  value: number;
  format?: (n: number) => string;
  color?: string;
}) {
  return (
    <div className="text-center px-1 min-w-0">
      <div className={`font-display text-xl font-semibold tabular-nums tracking-tight ${color ?? 'text-squishy-dark'}`}>
        <CountUp value={value} format={format} />
      </div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5 truncate">{label}</div>
    </div>
  );
}

function SettingsPanel() {
  const { data: me } = useMe();
  const update = useUpdateProfile();
  const [firstName, setFirstName] = useState(me?.user.firstName ?? '');
  const [lastName, setLastName]   = useState(me?.user.lastName ?? '');
  const [handle, setHandle]       = useState(me?.user.customHandle ?? '');
  const [mode, setMode]           = useState(me?.user.displayNameMode ?? 'real_name');

  if (!me) return null;

  const dirty =
    firstName !== me.user.firstName ||
    lastName  !== me.user.lastName ||
    handle    !== (me.user.customHandle ?? '') ||
    mode      !== me.user.displayNameMode;

  return (
    <section className={`${CARD} p-6 space-y-4`}>
      <h2 className="font-display text-base font-semibold text-squishy-dark tracking-tight">Settings</h2>

      <div className="grid grid-cols-2 gap-3">
        <Field label="First name">
          <input value={firstName} onChange={(e) => setFirstName(e.target.value)}
                 className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm focus:outline-none focus:border-squishy-teal focus:bg-white transition-colors" />
        </Field>
        <Field label="Last name">
          <input value={lastName} onChange={(e) => setLastName(e.target.value)}
                 className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm focus:outline-none focus:border-squishy-teal focus:bg-white transition-colors" />
        </Field>
      </div>

      <Field label="Display style">
        <div className="flex flex-wrap gap-2 text-sm">
          {(['real_name', 'custom', 'anonymous'] as const).map((m) => (
            <Pressable key={m}
                    haptic="selection"
                    onClick={() => setMode(m)}
                    className={`px-4 min-h-[44px] rounded-full border text-sm font-medium transition-colors ${
                      mode === m
                        ? 'bg-squishy-primary border-squishy-primary text-white shadow-pop'
                        : 'border-slate-200 text-slate-600 active:bg-slate-50'
                    }`}>
              {m === 'real_name' ? `${me.user.firstName} ${me.user.lastName[0]}.`
                : m === 'custom' ? '@username' : 'Anonymous'}
            </Pressable>
          ))}
        </div>
      </Field>

      {mode === 'custom' && (
        <Field label="Custom username">
          <input value={handle} onChange={(e) => setHandle(e.target.value.toLowerCase())}
                 placeholder="e.g. nurse_sarah"
                 className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5 text-sm focus:outline-none focus:border-squishy-teal focus:bg-white transition-colors" />
          <p className="text-xs text-slate-500 mt-1">Letters, numbers, underscores, hyphens. 2-50 chars.</p>
        </Field>
      )}

      <Pressable
        haptic="medium"
        disabled={!dirty || update.isPending}
        onClick={() => update.mutate({
          firstName, lastName,
          displayNameMode: mode,
          customHandle: mode === 'custom' ? handle : null,
        } as any)}
        className="bg-squishy-primary text-white px-5 min-h-[44px] rounded-xl text-sm font-semibold shadow-pop disabled:opacity-50 disabled:shadow-none"
      >
        {update.isPending ? 'Saving…' : 'Save changes'}
      </Pressable>
    </section>
  );
}

function AppearancePanel() {
  const [pref, setPref] = useState<ThemePreference>(() => getThemePreference());

  const choose = (p: ThemePreference) => {
    setPref(p);
    setThemePreference(p);
  };

  const options: { value: ThemePreference; label: string; icon: React.ReactNode }[] = [
    {
      value: 'light',
      label: 'Light',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
        </svg>
      ),
    },
    {
      value: 'dark',
      label: 'Dark',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd" />
        </svg>
      ),
    },
    {
      value: 'system',
      label: 'System',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
          <path fillRule="evenodd" d="M2.25 5.25a3 3 0 013-3h13.5a3 3 0 013 3V15a3 3 0 01-3 3h-3v.257c0 .597.237 1.17.659 1.591l.621.622a.75.75 0 01-.53 1.28h-9a.75.75 0 01-.53-1.28l.621-.622a2.25 2.25 0 00.659-1.59V18h-3a3 3 0 01-3-3V5.25zm3-1.5a1.5 1.5 0 00-1.5 1.5v9.75c0 .398.158.779.44 1.06.28.282.662.44 1.06.44h13.5a1.5 1.5 0 001.5-1.5V5.25a1.5 1.5 0 00-1.5-1.5H5.25z" clipRule="evenodd" />
        </svg>
      ),
    },
  ];

  return (
    <section className={`${CARD} p-6`}>
      <h2 className="font-display text-base font-semibold text-squishy-dark tracking-tight">Appearance</h2>
      <p className="text-sm text-slate-500 mt-1">Choose how Squishy School looks. System follows your device setting.</p>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {options.map((o) => (
          <Pressable
            key={o.value}
            haptic="selection"
            onClick={() => choose(o.value)}
            className={`flex flex-col items-center gap-1.5 py-3 min-h-[44px] rounded-2xl border-2 transition-colors ${
              pref === o.value
                ? 'border-squishy-primary bg-teal-50 text-squishy-tealDark shadow-glow'
                : 'border-slate-200 text-slate-500'
            }`}
          >
            {o.icon}
            <span className="text-xs font-semibold">{o.label}</span>
          </Pressable>
        ))}
      </div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-500 uppercase tracking-wide">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
